import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useOperator } from '@/lib/operator-context';
import { apiFetch } from '@/lib/operator-api';
import {
  Phone,
  Wifi,
  WifiOff,
  Shield,
  ShieldCheck,
  ShieldAlert,
  Activity,
  RefreshCw,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Clock,
  Send,
  MessageSquare,
  Database,
  Server,
  CreditCard,
  ChevronDown,
  ChevronUp,
  Zap,
  TrendingUp,
  ExternalLink,
  Circle,
} from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';

// ============================================================================
// Types
// ============================================================================

type ConnectionStatus = 'PENDING' | 'CONNECTED' | 'DISCONNECTED' | 'SUSPENDED' | 'BANNED';
type QualityRating = 'GREEN' | 'YELLOW' | 'RED';

interface ConnectionInfo {
  status: ConnectionStatus;
  qualityRating: QualityRating;
  displayPhone: string | null;
  verifiedName: string | null;
  connectedAt: string | null;
  lastVerifiedAt: string | null;
  disconnectedAt: string | null;
  errorMessage: string | null;
  webhookVerified: boolean;
  wabaId: string | null;
  phoneNumberId: string | null;
}

interface HealthCheck {
  status: string;
  database: { status: string; latency?: number };
  redis: { status: string; latency?: number };
  whatsapp: { status: string; latency?: number };
  mercadopago: { status: string; latency?: number };
}

interface MessageThroughput {
  today: number;
  thisWeek: number;
  thisMonth: number;
  avgResponseTime: string;
}

interface RateLimitInfo {
  messagesUsed: number;
  dailyLimit: number;
  templateLimit: number;
  templatesUsed: number;
  resetAt: string;
}

interface TimelineEvent {
  id: string;
  type: 'connected' | 'disconnected' | 'quality_change' | 'webhook_verified' | 'rate_limited';
  message: string;
  timestamp: string;
}

// ============================================================================
// Helper Functions
// ============================================================================

function getStatusConfig(status: ConnectionStatus): {
  color: string;
  bgColor: string;
  borderColor: string;
  icon: typeof CheckCircle2;
  label: string;
} {
  switch (status) {
    case 'CONNECTED':
      return {
        color: 'text-emerald-600 dark:text-emerald-400',
        bgColor: 'bg-emerald-500/10',
        borderColor: 'border-emerald-500/30',
        icon: CheckCircle2,
        label: 'connected',
      };
    case 'PENDING':
      return {
        color: 'text-amber-600 dark:text-amber-400',
        bgColor: 'bg-amber-500/10',
        borderColor: 'border-amber-500/30',
        icon: Clock,
        label: 'pending',
      };
    case 'DISCONNECTED':
      return {
        color: 'text-red-600 dark:text-red-400',
        bgColor: 'bg-red-500/10',
        borderColor: 'border-red-500/30',
        icon: WifiOff,
        label: 'disconnected',
      };
    case 'SUSPENDED':
      return {
        color: 'text-orange-600 dark:text-orange-400',
        bgColor: 'bg-orange-500/10',
        borderColor: 'border-orange-500/30',
        icon: ShieldAlert,
        label: 'suspended',
      };
    case 'BANNED':
      return {
        color: 'text-red-700 dark:text-red-500',
        bgColor: 'bg-red-600/10',
        borderColor: 'border-red-600/30',
        icon: XCircle,
        label: 'banned',
      };
    default:
      return {
        color: 'text-muted-foreground',
        bgColor: 'bg-muted/50',
        borderColor: 'border-border',
        icon: Circle,
        label: 'unknown',
      };
  }
}

function getQualityConfig(rating: QualityRating): {
  color: string;
  bgColor: string;
  borderColor: string;
  icon: typeof ShieldCheck;
  description: string;
} {
  switch (rating) {
    case 'GREEN':
      return {
        color: 'text-emerald-600 dark:text-emerald-400',
        bgColor: 'bg-emerald-500/10',
        borderColor: 'border-emerald-500/30',
        icon: ShieldCheck,
        description: 'qualityGreenDesc',
      };
    case 'YELLOW':
      return {
        color: 'text-amber-600 dark:text-amber-400',
        bgColor: 'bg-amber-500/10',
        borderColor: 'border-amber-500/30',
        icon: Shield,
        description: 'qualityYellowDesc',
      };
    case 'RED':
      return {
        color: 'text-red-600 dark:text-red-400',
        bgColor: 'bg-red-500/10',
        borderColor: 'border-red-500/30',
        icon: ShieldAlert,
        description: 'qualityRedDesc',
      };
    default:
      return {
        color: 'text-muted-foreground',
        bgColor: 'bg-muted/50',
        borderColor: 'border-border',
        icon: Shield,
        description: 'qualityUnknownDesc',
      };
  }
}

function getHealthStatusConfig(status: string): {
  color: string;
  bgColor: string;
  icon: typeof CheckCircle2;
} {
  switch (status?.toLowerCase()) {
    case 'ok':
    case 'healthy':
    case 'connected':
      return { color: 'text-emerald-600', bgColor: 'bg-emerald-500/10', icon: CheckCircle2 };
    case 'degraded':
    case 'slow':
      return { color: 'text-amber-600', bgColor: 'bg-amber-500/10', icon: AlertTriangle };
    case 'down':
    case 'error':
    case 'disconnected':
      return { color: 'text-red-600', bgColor: 'bg-red-500/10', icon: XCircle };
    default:
      return { color: 'text-muted-foreground', bgColor: 'bg-muted/50', icon: Circle };
  }
}

function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return '--';
  const date = new Date(dateStr);
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatRelativeTime(dateStr: string | null): string {
  if (!dateStr) return '--';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 30) return `${diffDays}d ago`;
  return formatDateTime(dateStr);
}

function getTimelineEventIcon(type: TimelineEvent['type']) {
  switch (type) {
    case 'connected':
      return Wifi;
    case 'disconnected':
      return WifiOff;
    case 'quality_change':
      return Shield;
    case 'webhook_verified':
      return CheckCircle2;
    case 'rate_limited':
      return AlertTriangle;
    default:
      return Circle;
  }
}

function getTimelineEventColor(type: TimelineEvent['type']): string {
  switch (type) {
    case 'connected':
      return 'text-emerald-600 bg-emerald-500/10';
    case 'disconnected':
      return 'text-red-600 bg-red-500/10';
    case 'quality_change':
      return 'text-amber-600 bg-amber-500/10';
    case 'webhook_verified':
      return 'text-blue-600 bg-blue-500/10';
    case 'rate_limited':
      return 'text-orange-600 bg-orange-500/10';
    default:
      return 'text-muted-foreground bg-muted/50';
  }
}

// ============================================================================
// Mock Data (placeholder until aggregate endpoints are available)
// ============================================================================

const MOCK_THROUGHPUT: MessageThroughput = {
  today: 142,
  thisWeek: 891,
  thisMonth: 3247,
  avgResponseTime: '1.2s',
};

const MOCK_RATE_LIMIT: RateLimitInfo = {
  messagesUsed: 142,
  dailyLimit: 1000,
  templateLimit: 250,
  templatesUsed: 38,
  resetAt: new Date(new Date().setHours(24, 0, 0, 0)).toISOString(),
};

const MOCK_TIMELINE: TimelineEvent[] = [
  {
    id: '1',
    type: 'connected',
    message: 'WhatsApp Business API connected successfully',
    timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: '2',
    type: 'webhook_verified',
    message: 'Webhook endpoint verified by Meta',
    timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: '3',
    type: 'quality_change',
    message: 'Quality rating changed: YELLOW to GREEN',
    timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: '4',
    type: 'rate_limited',
    message: 'Rate limit reached (1000/day) -- throttled for 2 minutes',
    timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: '5',
    type: 'disconnected',
    message: 'Connection lost -- automatic reconnection in progress',
    timestamp: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: '6',
    type: 'connected',
    message: 'Reconnected after scheduled maintenance',
    timestamp: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000 + 10 * 60 * 1000).toISOString(),
  },
];

// ============================================================================
// Main Component
// ============================================================================

function PhoneHealthPage() {
  const { t } = useTranslation();
  const { operator, isLoading: authLoading, selectedBusinessId, selectedBusiness } = useOperator();

  const [connection, setConnection] = useState<ConnectionInfo | null>(null);
  const [healthCheck, setHealthCheck] = useState<HealthCheck | null>(null);
  const [throughput] = useState<MessageThroughput>(MOCK_THROUGHPUT);
  const [rateLimit] = useState<RateLimitInfo>(MOCK_RATE_LIMIT);
  const [timeline] = useState<TimelineEvent[]>(MOCK_TIMELINE);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [isHealthExpanded, setIsHealthExpanded] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Fetch connection status
  const fetchConnectionStatus = useCallback(async (signal?: AbortSignal) => {
    if (!selectedBusinessId) return;
    try {
      const response = await apiFetch(
        `/api/operator/onboarding/connection-status?businessId=${selectedBusinessId}`,
        { signal }
      );
      if (signal?.aborted) return;
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      if (data.success && data.data) {
        setConnection(data.data);
      } else if (data.connection) {
        setConnection(data.connection);
      } else {
        // Fallback: treat entire response as connection info if it has a status field
        if (data.status) {
          setConnection(data);
        }
      }
      setFetchError(null);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      console.error('Failed to fetch connection status:', err);
      if (!signal?.aborted) setFetchError(err instanceof Error ? err.message : 'Failed to fetch connection status');
    }
  }, [selectedBusinessId]);

  // Fetch health checks
  const fetchHealthChecks = useCallback(async (signal?: AbortSignal) => {
    try {
      const API_URL = import.meta.env.VITE_API_URL || '';
      const response = await fetch(`${API_URL}/health`, { signal });
      if (signal?.aborted) return;
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setHealthCheck(data);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      console.error('Failed to fetch health checks:', err);
      // Non-critical -- health panel will show unknown status
    }
  }, []);

  // Initial load
  useEffect(() => {
    if (!operator) return;
    const controller = new AbortController();

    async function load() {
      setLoading(true);
      await Promise.all([fetchConnectionStatus(controller.signal), fetchHealthChecks(controller.signal)]);
      if (!controller.signal.aborted) setLoading(false);
    }
    load();

    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchConnectionStatus(controller.signal);
      fetchHealthChecks(controller.signal);
    }, 30000);

    return () => {
      controller.abort();
      clearInterval(interval);
    };
  }, [operator, selectedBusinessId, fetchConnectionStatus, fetchHealthChecks]);

  // Test connection handler
  const handleTestConnection = useCallback(async () => {
    setTestingConnection(true);
    try {
      await fetchConnectionStatus();
      await fetchHealthChecks();
    } finally {
      setTimeout(() => setTestingConnection(false), 1000);
    }
  }, [fetchConnectionStatus, fetchHealthChecks]);

  // Refresh handler
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await Promise.all([fetchConnectionStatus(), fetchHealthChecks()]);
    } finally {
      setTimeout(() => setRefreshing(false), 800);
    }
  }, [fetchConnectionStatus, fetchHealthChecks]);

  // Loading state
  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!operator) {
    return null;
  }

  const statusConfig = connection
    ? getStatusConfig(connection.status)
    : getStatusConfig('DISCONNECTED');
  const qualityConfig = connection
    ? getQualityConfig(connection.qualityRating)
    : getQualityConfig('GREEN');
  const StatusIcon = statusConfig.icon;
  const QualityIcon = qualityConfig.icon;

  const rateLimitPercent = Math.round((rateLimit.messagesUsed / rateLimit.dailyLimit) * 100);
  const templatePercent = Math.round((rateLimit.templatesUsed / rateLimit.templateLimit) * 100);

  return (
    <div className="p-6 space-y-6">
      {/* Error Banner */}
      {fetchError && (
        <div className="rounded-lg border border-destructive p-4">
          <p className="text-destructive text-sm">{fetchError}</p>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Phone className="h-6 w-6 text-[hsl(160,84%,39%)]" />
            {t('phoneHealth.title')}
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            {selectedBusiness ? selectedBusiness.name : t('common.allBusinesses')}
            {' -- '}
            {t('phoneHealth.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleTestConnection}
            disabled={testingConnection}
            className="gap-2"
          >
            {testingConnection ? (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
            ) : (
              <Zap className="h-4 w-4" />
            )}
            {t('phoneHealth.testConnection')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            className="gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            {t('phoneHealth.refreshStatus')}
          </Button>
        </div>
      </div>

      {/* Connection Status + Quality Rating */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Connection Status Card */}
        <Card className={`border-2 ${statusConfig.borderColor}`}>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Wifi className="h-5 w-5 text-[hsl(160,84%,39%)]" />
              {t('phoneHealth.connectionStatus')}
            </CardTitle>
            <CardDescription>{t('phoneHealth.connectionStatusDesc')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Status indicator */}
            <div className={`flex items-center gap-3 p-4 rounded-xl ${statusConfig.bgColor}`}>
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${statusConfig.bgColor}`}>
                <StatusIcon className={`h-6 w-6 ${statusConfig.color}`} />
              </div>
              <div>
                <div className={`text-lg font-bold ${statusConfig.color}`}>
                  {t(`phoneHealth.status.${statusConfig.label}`)}
                </div>
                {connection?.errorMessage && (
                  <p className="text-sm text-destructive mt-0.5">{connection.errorMessage}</p>
                )}
              </div>
            </div>

            {/* Details grid */}
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 rounded-lg bg-muted/30">
                <p className="text-xs text-muted-foreground">{t('phoneHealth.phoneNumber')}</p>
                <p className="font-semibold text-sm mt-0.5">
                  {connection?.displayPhone || '--'}
                </p>
              </div>
              <div className="p-3 rounded-lg bg-muted/30">
                <p className="text-xs text-muted-foreground">{t('phoneHealth.verifiedName')}</p>
                <p className="font-semibold text-sm mt-0.5">
                  {connection?.verifiedName || '--'}
                </p>
              </div>
              <div className="p-3 rounded-lg bg-muted/30">
                <p className="text-xs text-muted-foreground">{t('phoneHealth.connectedSince')}</p>
                <p className="font-semibold text-sm mt-0.5">
                  {formatDateTime(connection?.connectedAt ?? null)}
                </p>
              </div>
              <div className="p-3 rounded-lg bg-muted/30">
                <p className="text-xs text-muted-foreground">{t('phoneHealth.lastVerified')}</p>
                <p className="font-semibold text-sm mt-0.5">
                  {formatRelativeTime(connection?.lastVerifiedAt ?? null)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quality Rating Card */}
        <Card className={`border-2 ${qualityConfig.borderColor}`}>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Shield className="h-5 w-5 text-[hsl(160,84%,39%)]" />
              {t('phoneHealth.qualityRating')}
            </CardTitle>
            <CardDescription>{t('phoneHealth.qualityRatingDesc')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Quality indicator */}
            <div className={`flex items-center gap-3 p-4 rounded-xl ${qualityConfig.bgColor}`}>
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${qualityConfig.bgColor}`}>
                <QualityIcon className={`h-6 w-6 ${qualityConfig.color}`} />
              </div>
              <div>
                <div className={`text-lg font-bold ${qualityConfig.color}`}>
                  {connection?.qualityRating || '--'}
                </div>
                <p className="text-sm text-muted-foreground">
                  {t(`phoneHealth.${qualityConfig.description}`)}
                </p>
              </div>
            </div>

            {/* Quality tier explanation */}
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-muted-foreground">
                {t('phoneHealth.qualityTiers')}
              </h4>
              {(['GREEN', 'YELLOW', 'RED'] as QualityRating[]).map((tier) => {
                const tierConfig = getQualityConfig(tier);
                const TierIcon = tierConfig.icon;
                const isActive = connection?.qualityRating === tier;
                return (
                  <div
                    key={tier}
                    className={`flex items-center gap-2 p-2 rounded-lg text-sm transition-colors ${
                      isActive ? tierConfig.bgColor + ' font-medium' : 'text-muted-foreground'
                    }`}
                  >
                    <TierIcon className={`h-4 w-4 ${isActive ? tierConfig.color : ''}`} />
                    <span className={isActive ? tierConfig.color : ''}>
                      {tier}
                    </span>
                    <span className="mx-1">--</span>
                    <span>{t(`phoneHealth.tier${tier}`)}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Message Throughput Cards */}
      <div>
        <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-[hsl(160,84%,39%)]" />
          {t('phoneHealth.messageThroughput')}
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t('phoneHealth.sentToday')}</CardTitle>
              <Send className="h-4 w-4 text-[hsl(160,84%,39%)]" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{throughput.today}</div>
              <p className="text-xs text-muted-foreground">{t('phoneHealth.messages')}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t('phoneHealth.sentThisWeek')}</CardTitle>
              <MessageSquare className="h-4 w-4 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{throughput.thisWeek}</div>
              <p className="text-xs text-muted-foreground">{t('phoneHealth.messages')}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t('phoneHealth.sentThisMonth')}</CardTitle>
              <Activity className="h-4 w-4 text-purple-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{throughput.thisMonth.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">{t('phoneHealth.messages')}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t('phoneHealth.avgResponseTime')}</CardTitle>
              <Clock className="h-4 w-4 text-amber-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{throughput.avgResponseTime}</div>
              <p className="text-xs text-muted-foreground">{t('phoneHealth.latency')}</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Webhook Status + Rate Limit */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Webhook Status */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Zap className="h-5 w-5 text-[hsl(160,84%,39%)]" />
              {t('phoneHealth.webhookStatus')}
            </CardTitle>
            <CardDescription>{t('phoneHealth.webhookStatusDesc')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 rounded-xl bg-muted/30">
              <div className="flex items-center gap-3">
                {connection?.webhookVerified ? (
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                    <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                  </div>
                ) : (
                  <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                    <XCircle className="h-5 w-5 text-red-600" />
                  </div>
                )}
                <div>
                  <p className="font-medium">
                    {connection?.webhookVerified
                      ? t('phoneHealth.webhookVerified')
                      : t('phoneHealth.webhookNotVerified')}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {t('phoneHealth.webhookEndpoint')}
                  </p>
                </div>
              </div>
              <Badge
                variant="outline"
                className={
                  connection?.webhookVerified
                    ? 'border-emerald-500/30 text-emerald-600 bg-emerald-500/5'
                    : 'border-red-500/30 text-red-600 bg-red-500/5'
                }
              >
                {connection?.webhookVerified ? t('phoneHealth.verified') : t('phoneHealth.unverified')}
              </Badge>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 rounded-lg bg-muted/30">
                <p className="text-xs text-muted-foreground">{t('phoneHealth.wabaId')}</p>
                <p className="font-mono text-xs mt-0.5 truncate">
                  {connection?.wabaId || '--'}
                </p>
              </div>
              <div className="p-3 rounded-lg bg-muted/30">
                <p className="text-xs text-muted-foreground">{t('phoneHealth.phoneNumberId')}</p>
                <p className="font-mono text-xs mt-0.5 truncate">
                  {connection?.phoneNumberId || '--'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Rate Limit Status */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Activity className="h-5 w-5 text-[hsl(160,84%,39%)]" />
              {t('phoneHealth.rateLimitStatus')}
            </CardTitle>
            <CardDescription>{t('phoneHealth.rateLimitDesc')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Daily messages */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{t('phoneHealth.dailyMessages')}</span>
                <span className="font-medium">
                  {rateLimit.messagesUsed} / {rateLimit.dailyLimit}
                </span>
              </div>
              <Progress
                value={rateLimitPercent}
                className="h-2"
              />
              <p className="text-xs text-muted-foreground">
                {rateLimitPercent}% {t('phoneHealth.used')}
              </p>
            </div>

            <Separator />

            {/* Template messages */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{t('phoneHealth.templateMessages')}</span>
                <span className="font-medium">
                  {rateLimit.templatesUsed} / {rateLimit.templateLimit}
                </span>
              </div>
              <Progress
                value={templatePercent}
                className="h-2"
              />
              <p className="text-xs text-muted-foreground">
                {templatePercent}% {t('phoneHealth.used')}
              </p>
            </div>

            <Separator />

            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="h-4 w-4" />
              <span>
                {t('phoneHealth.resetsAt', {
                  time: new Date(rateLimit.resetAt).toLocaleTimeString('pt-BR', {
                    hour: '2-digit',
                    minute: '2-digit',
                  }),
                })}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* API Health Checks */}
      <Card>
        <CardHeader
          className="cursor-pointer select-none"
          onClick={() => setIsHealthExpanded(!isHealthExpanded)}
        >
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Server className="h-5 w-5 text-[hsl(160,84%,39%)]" />
                {t('phoneHealth.apiHealthChecks')}
              </CardTitle>
              <CardDescription>{t('phoneHealth.apiHealthChecksDesc')}</CardDescription>
            </div>
            <div className="flex items-center gap-3">
              {healthCheck && (
                <Badge
                  variant="outline"
                  className={
                    healthCheck.status === 'ok'
                      ? 'border-emerald-500/30 text-emerald-600 bg-emerald-500/5'
                      : 'border-red-500/30 text-red-600 bg-red-500/5'
                  }
                >
                  {healthCheck.status === 'ok'
                    ? t('phoneHealth.allSystemsOperational')
                    : t('phoneHealth.issuesDetected')}
                </Badge>
              )}
              {isHealthExpanded ? (
                <ChevronUp className="h-5 w-5 text-muted-foreground" />
              ) : (
                <ChevronDown className="h-5 w-5 text-muted-foreground" />
              )}
            </div>
          </div>
        </CardHeader>
        {isHealthExpanded && (
          <CardContent className="pt-0">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Database */}
              <HealthCheckItem
                name={t('phoneHealth.database')}
                icon={Database}
                status={healthCheck?.database?.status || 'unknown'}
                latency={healthCheck?.database?.latency}
              />
              {/* Redis */}
              <HealthCheckItem
                name={t('phoneHealth.redis')}
                icon={Server}
                status={healthCheck?.redis?.status || 'unknown'}
                latency={healthCheck?.redis?.latency}
              />
              {/* WhatsApp API */}
              <HealthCheckItem
                name={t('phoneHealth.whatsappApi')}
                icon={MessageSquare}
                status={healthCheck?.whatsapp?.status || 'unknown'}
                latency={healthCheck?.whatsapp?.latency}
              />
              {/* MercadoPago */}
              <HealthCheckItem
                name={t('phoneHealth.mercadoPago')}
                icon={CreditCard}
                status={healthCheck?.mercadopago?.status || 'unknown'}
                latency={healthCheck?.mercadopago?.latency}
              />
            </div>
          </CardContent>
        )}
      </Card>

      {/* Quick Actions + Connection History Timeline */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick Actions */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Zap className="h-5 w-5 text-[hsl(160,84%,39%)]" />
              {t('phoneHealth.quickActions')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button
              variant="outline"
              className="w-full justify-start gap-3 h-11 border-[hsl(160,60%,18%)]/20 hover:bg-[hsl(160,60%,18%)]/5"
              onClick={handleTestConnection}
              disabled={testingConnection}
            >
              {testingConnection ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
              ) : (
                <Zap className="h-4 w-4 text-[hsl(160,84%,39%)]" />
              )}
              {t('phoneHealth.testConnection')}
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start gap-3 h-11 border-[hsl(160,60%,18%)]/20 hover:bg-[hsl(160,60%,18%)]/5"
              onClick={handleRefresh}
              disabled={refreshing}
            >
              <RefreshCw className={`h-4 w-4 text-[hsl(160,84%,39%)] ${refreshing ? 'animate-spin' : ''}`} />
              {t('phoneHealth.refreshStatus')}
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start gap-3 h-11 border-[hsl(160,60%,18%)]/20 hover:bg-[hsl(160,60%,18%)]/5"
              onClick={() => {
                // Navigate to audit logs with WhatsApp filter
                window.location.href = '/operator/compliance';
              }}
            >
              <ExternalLink className="h-4 w-4 text-[hsl(160,84%,39%)]" />
              {t('phoneHealth.viewLogs')}
            </Button>
          </CardContent>
        </Card>

        {/* Connection History Timeline */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="h-5 w-5 text-[hsl(160,84%,39%)]" />
              {t('phoneHealth.connectionHistory')}
            </CardTitle>
            <CardDescription>{t('phoneHealth.connectionHistoryDesc')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-5 top-0 bottom-0 w-px bg-border" />

              <div className="space-y-4">
                {timeline.map((event) => {
                  const EventIcon = getTimelineEventIcon(event.type);
                  const eventColor = getTimelineEventColor(event.type);

                  return (
                    <div key={event.id} className="relative flex items-start gap-4 pl-2">
                      <div
                        className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${eventColor}`}
                      >
                        <EventIcon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0 pb-1">
                        <p className="text-sm font-medium text-foreground">{event.message}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {formatRelativeTime(event.timestamp)}
                          <span className="mx-1.5">--</span>
                          {formatDateTime(event.timestamp)}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ============================================================================
// Sub-components
// ============================================================================

function HealthCheckItem({
  name,
  icon: Icon,
  status,
  latency,
}: {
  name: string;
  icon: React.ComponentType<{ className?: string }>;
  status: string;
  latency?: number;
}) {
  const config = getHealthStatusConfig(status);
  const StatusIcon = config.icon;

  return (
    <div className={`p-4 rounded-xl border ${config.bgColor} transition-colors`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon className={`h-4 w-4 ${config.color}`} />
          <span className="text-sm font-medium">{name}</span>
        </div>
        <StatusIcon className={`h-4 w-4 ${config.color}`} />
      </div>
      <div className="flex items-center justify-between">
        <Badge variant="outline" className={`text-xs ${config.color} border-current/20`}>
          {status}
        </Badge>
        {latency != null && (
          <span className="text-xs text-muted-foreground">{latency}ms</span>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Route Export
// ============================================================================

export const Route = createFileRoute('/_auth/operator/phone-health')({
  component: PhoneHealthPage,
});
